/**
 * `art/hq/anchors.json`: per-model metadata computed from the packed GLBs (height, extents, clip names, and from the
 * v4 ground kit on: `blocks` for whole-cell blockers, `wind` for vertex-wind meshes, `pbr`). Missing entries read as
 * "no flags", so older sets keep working.
 */
export interface HqAnchor {
  height: number; min_y: number; half_x: number; half_z: number; clips: string[]; blocks?: boolean; wind?: boolean; pbr?: boolean;
  /** Skin-kit attachment sockets (towers; tools/art/skins/sockets.mjs, render/skins/kit/sockets.ts). */
  sockets?: Record<string, HqSocket>;
  /** Big kits' fit envelopes (towers; tools/art/skins/sockets.mjs, render/skins/kit/fit.ts): roof, drum, plinth, gallery, tower. */
  dims?: Record<string, HqDim>;
  /** Measured gaits, seconds a cycle and the ground covered in one at scale 1: a pet's (art/unitkit/pet_gait.py), a raider's `walk` (art/unitkit/enemy_gait.mjs). */
  gait?: Record<string, { cycle: number; stride: number }>;
}
/** A mount point in glTF model space (front −z): the part it rides, a surface point, the outward normal. */
export interface HqSocket { part: string; p: readonly number[]; n: readonly number[] }
/**
 * A part's envelope in glTF model space: the part a fitted piece rides, its bottom centre `c` on its axis, its height
 * `h`, its widest radius `r` from that axis, its radius over the bottom and top tenths (`r0`, `r1`: the taper) and its
 * box half-extents `rx`, `rz`.
 */
export interface HqDim { part: string; c: readonly number[]; h: number; r: number; r0: number; r1: number; rx: number; rz: number }

export class HqAnchors {
  private readonly byName = new Map<string, HqAnchor>();

  static async load(url: string): Promise<HqAnchors> {
    const out = new HqAnchors();
    try {
      const res = await fetch(url);
      if (res.ok) for (const [name, a] of Object.entries((await res.json()) as Record<string, HqAnchor>)) out.byName.set(name, a);
    } catch { /* no anchors: every model uses its defaults */ }
    return out;
  }

  get(name: string): HqAnchor | undefined { return this.byName.get(name.replace(/^hq:/, "")); }
  blocks(name: string): boolean { return this.get(name)?.blocks === true; }
  /** Names with `blocks: true` matching a prefix, e.g. `p_rock_` or `p_tree_`. */
  blockers(prefix: string): string[] { return [...this.byName.entries()].filter(([n, a]) => a.blocks && n.startsWith(prefix)).map(([n]) => n); }
  get size(): number { return this.byName.size; }
}
